import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { layoutConfigurationApi } from 'store/api';
import { groupItemIdsByParentId } from './layout-configurations/Utils';
import {
  IBaseLayout,
  IFindLayoutConfigurationParams,
} from 'api/digifi/layout/LayoutConfigurationApi';
import rejectWithValue from 'utils/rejectWithValue';

export enum LayoutConfigurationActionType {
  GetLayoutConfiguration = 'layoutConfigurations/getLayoutConfiguration',
  UpdateLayoutConfiguration = 'layoutConfigurations/updateLayoutConfiguration',
}

export interface IItemData {
  id: string;
  position: number;
}

export interface ILayoutConfiguration {
  sortedGroups: Array<IItemData>;
  sortedCardsByGroup: Record<string, Array<IItemData>>;
  sortedVariableConfigurationsByCard: Record<string, Array<IItemData>>;
  isValid?: boolean;
}

export interface ILayoutConfigurationState {
  configurationsByReferenceKey: Record<string, ILayoutConfiguration>;
}

export const initialState: ILayoutConfigurationState = {
  configurationsByReferenceKey: {},
};

export const getLayoutConfiguration = createAsyncThunk<
  IBaseLayout,
  IFindLayoutConfigurationParams,
  { fulfilledMeta: { referenceKey: string } }
>(
  LayoutConfigurationActionType.GetLayoutConfiguration,
  async (params, thunkApi) => {
    try {
      return thunkApi.fulfillWithValue(await layoutConfigurationApi.find(params), {
        referenceKey: params.referenceType + (params.reference ? `-${params.reference}` : ''),
      });
    } catch (error) {
      return rejectWithValue(error, thunkApi);
    }
  },
);

const layoutConfigurationsSlice = createSlice({
  name: 'layoutConfigurationsSlice',
  initialState,
  reducers: {
    removeLayoutConfigurations(state) {
      state.configurationsByReferenceKey = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLayoutConfiguration.fulfilled, (state, action) => {
      state.configurationsByReferenceKey = {
        ...state.configurationsByReferenceKey,
        [action.meta.referenceKey]: {
          sortedGroups: action.payload.groups.map((group) => ({ id: group.id, position: group.position })),
          sortedCardsByGroup: groupItemIdsByParentId(action.payload.cards, 'group'),
          sortedVariableConfigurationsByCard: groupItemIdsByParentId(
            action.payload.variables,
            'cardId',
          ),
        },
      };
    });
  },
});

export const { removeLayoutConfigurations } = layoutConfigurationsSlice.actions;

export default layoutConfigurationsSlice.reducer;
